import { useEffect } from 'react'
import { useBlocker } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAudioStore } from '../store/audioStore'

/**
 * Asks for confirmation before a page unload or an in-app route change while a
 * recording is active, so no page transition can silently kill the conversation (MD-1312).
 */
export const useRecordingNavigationGuard = (): void => {
    const { t } = useTranslation()
    const isRecording = useAudioStore((state) => !!state.mediaRecorder)

    useBlocker(({ currentLocation, nextLocation }) => {
        if (!isRecording || currentLocation.pathname === nextLocation.pathname) return false
        return !window.confirm(t('encounter.leave_while_recording'))
    })

    useEffect(() => {
        if (!isRecording) return

        // Native pull-to-refresh would reload mid-recording; iOS Safari ignores beforeunload, so block the gesture
        const previousHtmlOverscroll = document.documentElement.style.overscrollBehaviorY
        const previousBodyOverscroll = document.body.style.overscrollBehaviorY
        document.documentElement.style.overscrollBehaviorY = 'none'
        document.body.style.overscrollBehaviorY = 'none'

        const warnBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault()
            // Older Chromium versions only show the dialog when returnValue is set
            event.returnValue = ''
        }

        window.addEventListener('beforeunload', warnBeforeUnload)
        return () => {
            document.documentElement.style.overscrollBehaviorY = previousHtmlOverscroll
            document.body.style.overscrollBehaviorY = previousBodyOverscroll
            window.removeEventListener('beforeunload', warnBeforeUnload)
        }
    }, [isRecording])
}
